import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { CertificationService } from "../../../../certification/services/certification-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { currentAndUpcomingBenefitPeriods } from "./current-and-upcoming-benefit-periods";
import { InputObservableValue } from "./input-observable";

/**
 * Generated class for the SelectInputComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */

@Component({
  selector: "select-input",
  templateUrl: "select-input.html",
})
export class SelectInputComponent implements OnInit, AfterViewInit, OnDestroy {
  // Configuration properties
  @Input() group: FormGroup;
  @Input() config;

  // Internal data model
  private emitRawData: Function = null;
  private observableSubscription: Subscription;
  id: string;
  componentId: string;
  label: any;
  modalLabel: string;
  labelColor: string;
  options: any[] = [];
  placeholder: string = "pleaseSelect";
  innerGroup: FormGroup;
  max: number;
  min: number;
  required: boolean;
  supportOther: boolean = false;
  supportOtherOverride: boolean;
  otherConfig: any;
  showOther: boolean = false;
  disabled: boolean;
  readonly: boolean = false;
  multiple: boolean = false;
  dataRows: any;
  observableHasHiddenOther: boolean = false;
  get hasToShowOtherOption() {
    return (
      (this.supportOther || this.supportOtherOverride) &&
      !this.observableHasHiddenOther
    );
  }

  constructor(
    public formBuilder: FormBuilder,
    private certificationService: CertificationService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.emitRawData = this.config.emitRawData;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined ? this.config.label : this.config.id;
    if (this.label === "false") {
      this.label = false;
    }
    this.modalLabel = this.config.label === false ? this.id : this.label;
    this.labelColor = this.config.labelColor;
    this.readonly = this.config.readonly;
    this.supportOther = this.config.supportOther;
    this.multiple = this.config.multiple === true;
    this.supportOtherOverride = this.config.supportOtherOverride;
    if (this.config.options) {
      this.options = this.config.options;
    }

    if (this.config.useObservable) {
      const observable = this.getObservableByName(this.config.useObservable);
      this.observableSubscription = observable.subscribe((result) => {
        this.options = result.options;
        this.observableHasHiddenOther = result.hideOther;
      });
    }

    if (this.config.placeholder) {
      this.placeholder = this.config.placeholder;
    }
    this.group.addControl(this.id, this.formBuilder.control(null));
    this.disabled = this.group.controls[this.id].disabled;

    this.innerGroup = this.formBuilder.group({});
    this.innerGroup.addControl("select", this.formBuilder.control(null));

    const validators = [];
    if (this.config.rules) {
      if (this.config.rules.required) {
        this.required = this.config.rules.required;
        validators.push(Validators.required);
      }
    }

    if (this.config.dataRows) {
      this.dataRows = this.config.dataRows;
    }

    var composedValidators: ValidatorFn = Validators.compose(validators);

    this.innerGroup.controls["select"].setValidators(composedValidators);

    this.otherConfig = {
      id: "other",
      label: false,
      placeholder: "specify",
      rules: this.config.otherRules,
      readonly: this.readonly,
    };
    if (this.otherConfig.rules && this.otherConfig.rules.required) {
      delete this.otherConfig.rules.required;
    }

    this.innerGroup.addControl("other", this.formBuilder.control(""));

    this.innerGroup.controls["other"].valueChanges.subscribe((data) => {
      if (data !== undefined && data !== "") {
        this.group.controls[this.id].setValue(this.getCurrentValue());
      }
    });

    this.innerGroup.controls["select"].valueChanges.subscribe((data) => {
      this.showOther = data === "other";
      if (!this.showOther) {
        this.innerGroup.controls["other"].setValue(null, { emitEvent: false });
      }
    });

    this.innerGroup.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setValue(this.getCurrentValue());
      this.group.markAsDirty();
      this.group.controls[this.id].setErrors(this.getErrors());
    });

    // DATA LOADING
    this.group.controls[this.id].valueChanges.subscribe((data) => {
      if (data != this.getCurrentValue()) {
        this.setInnerValue(data);
      }

      this.supportOtherOverride = this.config.supportOtherOverride;

      let outerDisabled = this.group.controls[this.id].disabled;
      if (outerDisabled !== this.disabled) {
        this.disabled = outerDisabled;
        if (this.disabled || this.readonly) {
          this.innerGroup.disable();
        } else {
          this.innerGroup.enable();
        }
      }
    });

    let initValue = this.group.controls[this.id].value;
    if (initValue !== null && initValue !== undefined) {
      this.setInnerValue(initValue);
    }
  }

  ngAfterViewInit(): void {
    this.group.controls[this.id].setErrors(this.getErrors());

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setErrors(this.getErrors());
    });
  }

  setInnerValue(data: string) {
    if (data === null) {
      let clearVal = {};
      clearVal["select"] = null;
      clearVal["other"] = null;
      this.innerGroup.setValue(clearVal);
    } else if (data || data == "0") {
      if (
        (this.supportOther || this.supportOtherOverride) &&
        !this.optionsContainsKey(data)
      ) {
        this.innerGroup.controls["other"].setValue(data);
        this.innerGroup.controls["select"].setValue("other");
      } else {
        this.innerGroup.controls["select"].setValue(data);
        this.innerGroup.controls["other"].setValue(undefined);
      }
    }
  }

  optionsContainsKey(key: string): boolean {
    for (let option of this.options) {
      if (option === key || option["value"] === key) {
        return true;
      }
    }
    return false;
  }

  getCurrentValue(): string {
    let selectValue = this.innerGroup.controls["select"].value;
    if (this.emitRawData) {
      this.emitRawData.call(null, {
        selectValue,
        otherText: this.innerGroup.controls["other"].value,
      });
    }
    if (this.supportOther || this.supportOtherOverride) {
      selectValue =
        selectValue == "other"
          ? this.innerGroup.controls["other"].value
          : selectValue;
      if (selectValue == "") {
        selectValue = undefined;
      }
    }
    return selectValue;
  }

  getErrors(): ValidationErrors {
    let errors = {};
    if (
      this.required &&
      (undefined === this.getCurrentValue() || null === this.getCurrentValue())
    ) {
      errors["required"] = true;
    }

    if (
      (this.supportOther || this.supportOtherOverride) &&
      this.innerGroup.controls["other"].invalid
    ) {
      errors["invalidOther"] = true;
    }

    if (
      this.dataRows &&
      this.config.rules &&
      this.config.rules.checkDuplicates
    ) {
      let parentValue = "";
      let value = this.getCurrentValue();
      if (this.config.rules && this.config.rules.parent) {
        parentValue = this.group.controls[this.config.rules.parent].value;
      }

      if (this.config.existingData === false) {
        for (let key of this.dataRows) {
          if (
            key.values[this.id] === value &&
            key.values[this.config.rules.parent] === parentValue
          ) {
            if (this.id === "task" && key.values.endDate === "-") {
              errors["task"] = true;
              break;
            }
          }
        }
      } else {
        let data = this.config.currentRow;
        for (let key of this.dataRows) {
          if (
            key.values[this.id] !== data[this.id] &&
            key.values[this.config.rules.parent] !== data[this.id]
          ) {
            if (
              key.values[this.id] === value &&
              key.values[this.config.rules.parent] === parentValue
            ) {
              if (this.id === "task" && key.values.endDate === "-") {
                errors["task"] = true;
                break;
              }
            }
          }
        }
      }
    }

    return Object.keys(errors).length == 0 ? undefined : errors;
  }

  getObservableByName(name: string): Observable<InputObservableValue> {
    switch (name) {
      case "currentAndUpcomingBenefitPeriods":
        return currentAndUpcomingBenefitPeriods(
          this.certificationService,
          this.stateManagementService
        );
      default:
        throw new Error(`Observable with name "${name}" not found`);
    }
  }

  ngOnDestroy(): void {
    if (this.observableSubscription) {
      this.observableSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/select-input/select-input.ts